
export { OrderForm };

type OnActiveModelChanged = (model: Model | null) => void;

type ErrorResponse = {
    code: string,
    data: {
        status: number;
    },
    message: string,
};

enum FormState {
    Idle,
    WaitingForResponse,
    Done
}

class Model {
    file: File;
    element: HTMLElement;
}

class OrderForm {
    form: HTMLFormElement;
    fileInput: HTMLInputElement;
    submitButton: HTMLButtonElement;
    errorMessage: HTMLElement;
    modelUpload: HTMLElement;
    modelTemplate: HTMLTemplateElement;

    models: Model[] = [];
    activeModel: Model | null = null;

    onActiveModelChanged: OnActiveModelChanged;

    constructor(form: HTMLFormElement, supportedFormats: string[], onActiveModelChanged: OnActiveModelChanged) {
        if (!(form instanceof HTMLFormElement)) {
            throw new TypeError('form must be of type HTMLFormElement');
        }

        form.addEventListener('submit', this.onSubmit.bind(this));

        let fileInput: HTMLInputElement = form.querySelector('input[type=file]')!;
        fileInput.setAttribute("accept", supportedFormats.map((fmt) => `.${fmt}`).join(","));
        fileInput.addEventListener('change', this.onFilesChanged.bind(this));

        this.form = form;
        this.fileInput = fileInput;
        this.submitButton = form.querySelector('button')!;
        this.errorMessage = form.querySelector('#error-message')!;
        this.modelUpload = form.querySelector('#model-upload')!;
        this.modelTemplate = form.querySelector('template#model-template')!;
        this.onActiveModelChanged = onActiveModelChanged;
    }

    setActiveModel(model: Model | null) {
        if (this.activeModel == model) {
            return;
        }

        if (this.activeModel) {
            this.activeModel.element.classList.remove('active');
        }
        if (model) {
            model.element.classList.add('active');
        }

        this.activeModel = model;
        this.onActiveModelChanged(model);
    }

    async onFilesChanged(event: Event) {
        let files: FileList = this.fileInput.files!;
        if (files.length == 0) {
            return;
        }

        this.fileInput.disabled = true;

        for (let file of files) {
            if (this.models.map((model) => model.file.name).includes(file.name)) {
                continue;
            }

            let element = this.modelTemplate.content.firstElementChild!.cloneNode(true) as HTMLElement;

            let model = new Model();
            model.file = file;
            model.element = element;
            this.models.push(model);

            element.addEventListener('click', () => this.onModelSelected(model));
            element.querySelector('#model-name')!.textContent = file.name;
            element.querySelector('#remove')!.addEventListener('click', (ev) => this.onModelRemoved(ev, model));

            this.modelUpload.insertBefore(element, this.modelUpload.querySelector('#load-model'));
        }

        if (!this.activeModel) {
            this.setActiveModel(this.models[0]);
        }

        this.fileInput.disabled = false;
    }

    async onModelSelected(model: Model) {
        this.setActiveModel(model);
    }

    async onModelRemoved(event: Event, model: Model) {
        event.stopPropagation();

        model.element.remove();
        let index = this.models.indexOf(model);
        this.models.splice(index, 1);

        if (this.activeModel == model) {
            this.setActiveModel(this.models[0]);
        }
    }

    async onSubmit(event: SubmitEvent) {
        event.preventDefault();
        if (!this.form.reportValidity()) {
            return;
        }

        this.errorMessage.style.display = 'none';
        this.setState(FormState.WaitingForResponse);

        let formData = new FormData(this.form);
        for (let model of this.models) {
            formData.append('model-files[]', model.file);
        }

        let response = await fetch(
            '/wp-json/ideolo3d-order-form/submit',
            {
                method: 'POST',
                body: formData,
            }
        );

        if (response.ok) {
            let redirectUrl = formData.get('redirect-url')?.toString();
            if (redirectUrl) {
                window.location.href = redirectUrl;
            }
            this.setState(FormState.Done);
            return;
        }

        let error: ErrorResponse = await response.json();
        this.errorMessage.style.display = 'initial';
        this.errorMessage.innerText = error.message;
        this.setState(FormState.Idle);
    }

    setState(state: FormState) {
        let buttonSpan: HTMLElement = this.submitButton.querySelector('span')!;
        let buttonLoader: HTMLElement = this.submitButton.querySelector('.loader')!;
        let buttonOk: HTMLElement = this.submitButton.querySelector('.ok')!;

        switch (state) {
            case FormState.Idle:
                this.submitButton.disabled = false;
                buttonSpan.style.display = 'initial';
                buttonLoader.style.display = 'none';
                buttonOk.style.display = 'none';
                break;
            case FormState.WaitingForResponse:
                this.submitButton.disabled = true;
                buttonSpan.style.display = 'none';
                buttonLoader.style.display = 'block';
                buttonOk.style.display = 'none';
                break;
            case FormState.Done:
                this.submitButton.disabled = true;
                buttonSpan.style.display = 'none';
                buttonLoader.style.display = 'none';
                buttonOk.style.display = 'initial';
                break;
        }
    }
}
